import React, { useCallback } from 'react';
import { isFunctionDataType, isSecretDataType } from '@glueapp/graph-types';
import type {
    DataConnection,
    DataValue,
    FunctionDefinition,
    GlueVariable,
    IntegrationAccess,
    NodeExecutionResult,
    NodeInstance,
    NodeInstanceDataPort,
    UserDefinedType,
} from '@glueapp/graph-types';
import { NODE_LIBRARY_DICTIONARY } from '@glueapp/graph-nodes';
import { PortValueEditor } from '@/NodeInstanceEditor/PortValueEditor';
import { IntegrationEditor } from '@/NodeInstanceEditor/IntegrationEditor';
import { VariableEditor } from '@/NodeInstanceEditor/VariableEditor';
import { WebhookEditor } from '@/NodeInstanceEditor/WebhookEditor';
import { ScheduleEditor } from '@/NodeInstanceEditor/ScheduleEditor/ScheduleEditor';
import { FunctionPortEditor } from '@/NodeInstanceEditor/FunctionPortEditor/FunctionPortEditor';
import { SecretPortEditor } from '@/NodeInstanceEditor/SecretPortEditor';
import { OutputValueOverlay } from './OutputValueOverlay';
import type { GraphReducerActions } from '@/GraphEditor/actions';
import type { Webhook } from '@/api/useWebhookApi';
import { setFunctionPortValue, createFunctionFromSignature, deleteFunction, updateFunction } from '@/GraphEditor/actions';

interface Props {
    nodeInstance: NodeInstance;
    portName: string;
    portSide: 'in' | 'out' | 'special';
    integrationAccesses: IntegrationAccess[];
    deleteIntegrationAccess: (integrationAccess: IntegrationAccess) => void;
    connections: DataConnection[];
    onValueChange: (
        nodeInstanceId: number,
        port: NodeInstanceDataPort,
        value: DataValue | undefined,
        userDefinedType?: UserDefinedType,
    ) => void;
    variables: GlueVariable[];
    setSelectedVariable: (name: string | undefined, nodeInstanceId: number) => void;
    addVariable: (newVariable: GlueVariable) => void;
    updateVariable: (oldName: string, variable: GlueVariable) => void;
    deleteVariable: (name: string) => void;
    webhooks: Webhook[];
    nodeInstancesWithPendingRequests: number[];
    createWebhook: (nodeInstanceId: number) => void;
    availableFunctions: FunctionDefinition[];
    dispatchGraphUpdate: (action: GraphReducerActions) => void;
    navigateToFunction: (functionId: string) => void;
    graphId: string;
    nodeExecutionResult?: NodeExecutionResult;
    onCloseOverlay?: () => void;
    onOpenSecretsPanel?: () => void;
}

export const PortOverlayContent: React.FC<Props> = ({
    nodeInstance,
    portName,
    portSide,
    integrationAccesses,
    deleteIntegrationAccess,
    connections,
    onValueChange,
    variables,
    setSelectedVariable,
    addVariable,
    updateVariable,
    deleteVariable,
    webhooks,
    nodeInstancesWithPendingRequests,
    createWebhook,
    availableFunctions,
    dispatchGraphUpdate,
    navigateToFunction,
    graphId,
    nodeExecutionResult,
    onCloseOverlay,
    onOpenSecretsPanel,
}) => {
    const node = NODE_LIBRARY_DICTIONARY[nodeInstance.nodeId];

    if (!node) return null;

    // Special overlay types
    switch (portName) {
        case '__integration__': {
            if (!node.integration) return null;
            const nodeIntegrationAccesses = integrationAccesses.filter(ia => ia.provider === node.integration!.provider);
            return (
                <IntegrationEditor
                    nodeInstance={nodeInstance}
                    integration={node.integration}
                    integrationAccesses={nodeIntegrationAccesses}
                    deleteIntegrationAccess={deleteIntegrationAccess}
                    dispatchGraphUpdate={dispatchGraphUpdate}
                />
            );
        }
        case '__schedule__':
            return <ScheduleEditor nodeInstance={nodeInstance} dispatchGraphUpdate={dispatchGraphUpdate} />;
        case '__webhook__': {
            const webhook = webhooks.find(w => w.nodeInstanceId === nodeInstance.id);
            return (
                <WebhookEditor
                    webhook={webhook}
                    webhookCreatePending={nodeInstancesWithPendingRequests.includes(nodeInstance.id)}
                    createWebhook={() => createWebhook(nodeInstance.id)}
                />
            );
        }
        case '__variable__': {
            const selectedVariable = nodeInstance.selectedVariable
                ? variables.find(v => v.name === nodeInstance.selectedVariable)
                : undefined;
            return (
                <VariableEditor
                    variables={variables}
                    selectedVariable={selectedVariable}
                    setSelectedVariable={(name: string | undefined) => setSelectedVariable(name, nodeInstance.id)}
                    addVariable={addVariable}
                    updateVariable={updateVariable}
                    deleteVariable={deleteVariable}
                />
            );
        }
    }

    // Regular port - find the port
    const port =
        portSide === 'in' ? nodeInstance.inPorts.find(p => p.name === portName) : nodeInstance.outPorts.find(p => p.name === portName);

    if (!port) return null;

    if (portSide === 'out') {
        if (nodeExecutionResult) {
            const outputValue = nodeExecutionResult.output?.[portName];
            const executionDecision = nodeExecutionResult.executionDecisions?.[portName];
            if (outputValue !== undefined || executionDecision !== undefined) {
                return (
                    <OutputValueOverlay value={outputValue} executionDecision={executionDecision} onClose={onCloseOverlay ?? (() => {})} />
                );
            }
        }
        return null;
    }

    // Check if port has a connection
    const hasConnection = connections.some(c => c.receivingNodeInstanceId === nodeInstance.id && c.receivingPortName === portName);

    // Handle secret ports (unwrap optional wrapper if present)
    const unwrappedType = port.type?.kind === 'optional' ? port.type.innerType : port.type;
    if (unwrappedType && isSecretDataType(unwrappedType)) {
        return (
            <SecretPortEditor
                portName={port.name}
                currentValue={nodeInstance.portValues[port.name]}
                graphId={graphId}
                hasConnection={hasConnection}
                onValueChange={value => onValueChange(nodeInstance.id, port, value)}
                onOpenSecretsPanel={onOpenSecretsPanel}
            />
        );
    }

    // Handle function ports
    if (port.type?.kind === 'function' && isFunctionDataType(port.type)) {
        return (
            <FunctionPortEditor
                portName={port.name}
                expectedType={port.type}
                currentValue={nodeInstance.portValues[port.name] as any}
                availableFunctions={availableFunctions}
                onFunctionSelect={funcId => {
                    dispatchGraphUpdate(setFunctionPortValue(nodeInstance.id, port.name, funcId));
                }}
                onFunctionClear={() => {
                    dispatchGraphUpdate(setFunctionPortValue(nodeInstance.id, port.name, undefined));
                }}
                onCreateFunction={functionName => {
                    if (port.type.kind === 'function') {
                        const inputPortDefs = Object.entries(port.type.inputTypes).map(([name, type]) => ({
                            id: name,
                            name,
                            type,
                        }));
                        const outputPortDefs = Object.entries(port.type.outputTypes).map(([name, type]) => ({
                            id: name,
                            name,
                            type,
                        }));
                        dispatchGraphUpdate(
                            createFunctionFromSignature(functionName, inputPortDefs, outputPortDefs, nodeInstance.id, port.name, undefined),
                        );
                    }
                }}
                onEditFunction={navigateToFunction}
                onDeleteFunction={funcId => {
                    dispatchGraphUpdate(deleteFunction(funcId));
                }}
                onFixFunctionCompatibility={fixedFunction => {
                    dispatchGraphUpdate(updateFunction(fixedFunction.id, fixedFunction));
                }}
            />
        );
    }

    // Regular port value editor
    return (
        <PortValueEditor
            inPort={port}
            portValue={nodeInstance.portValues[port.name]}
            portUserDefinedType={nodeInstance.portUserDefinedTypes[port.name]}
            onValueChange={(inPort, value, type) => {
                onValueChange(nodeInstance.id, inPort, value, type);
            }}
            hasConnection={hasConnection}
        />
    );
};
